import { useState, useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";

export function Login() {
  const { login, publicLogin, requestMagicLink, error } = useAuth();
  const [password, setPassword] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [magicEmail, setMagicEmail] = useState("");
  const [magicLinkSent, setMagicLinkSent] = useState(false);
  const [isSendingLink, setIsSendingLink] = useState(false);
  const [urlError, setUrlError] = useState<string | null>(null);

  // Check URL for error params (from magic link verify or OAuth)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const errorParam = params.get("error");
    if (errorParam) {
      const messages: Record<string, string> = {
        invalid_link: "This login link is invalid or has expired.",
        oauth_error: "Google sign-in failed. Please try again.",
        missing_params: "Google sign-in failed. Please try again.",
        unauthorized: "This email is not authorized. Ask an admin for an invite.",
        auth_failed: "Authentication failed. Please try again.",
      };
      setUrlError(messages[errorParam] || "An error occurred. Please try again.");
      // Clean URL
      window.history.replaceState({}, "", window.location.pathname);
    }
  }, []);

  const handlePublicLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!password.trim()) return;

    setIsLoading(true);
    await publicLogin(password);
    setIsLoading(false);
  };

  const handleMagicLink = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!magicEmail.trim()) return;

    setIsSendingLink(true);
    const success = await requestMagicLink(magicEmail);
    if (success) {
      setMagicLinkSent(true);
    }
    setIsSendingLink(false);
  };

  const displayError = urlError || error;

  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-4">
      <div className="w-full max-w-sm">
        <div className="text-center mb-10">
          <h1 className="text-3xl font-medium text-foreground mb-2">
            Idea Factory
          </h1>
          <p className="text-muted text-sm">
            Documentary pre-production workspace
          </p>
        </div>

        <div className="bg-surface border border-border rounded-lg p-6">
          <h2 className="text-base font-medium text-foreground mb-5 text-center">
            Sign in to continue
          </h2>

          {displayError && (
            <div className="mb-4 p-3 bg-red-500/10 border border-red-500/20 rounded text-red-400 text-sm">
              {displayError}
            </div>
          )}

          <button
            onClick={login}
            className="w-full flex items-center justify-center gap-3 bg-foreground hover:bg-gray-100 text-background font-medium py-2.5 px-4 rounded-lg transition-colors"
          >
            <GoogleIcon />
            Sign in with Google
          </button>

          <div className="relative my-5">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-border"></div>
            </div>
            <div className="relative flex justify-center text-xs">
              <span className="bg-surface px-2 text-muted">or</span>
            </div>
          </div>

          {magicLinkSent ? (
            <div className="text-center py-4">
              <div className="text-primary-400 text-sm font-medium mb-2">
                Check your email
              </div>
              <p className="text-muted-foreground text-xs mb-3">
                We sent a login link to <strong className="text-muted">{magicEmail}</strong>
              </p>
              <button
                onClick={() => {
                  setMagicLinkSent(false);
                  setMagicEmail("");
                }}
                className="text-primary-500 hover:text-primary-400 text-xs transition-colors"
              >
                Use a different email
              </button>
            </div>
          ) : (
            <form onSubmit={handleMagicLink} className="space-y-3">
              <input
                type="email"
                value={magicEmail}
                onChange={(e) => setMagicEmail(e.target.value)}
                placeholder="Enter your email"
                className="w-full bg-surface-elevated border border-border rounded-lg px-4 py-2.5 text-foreground placeholder:text-muted focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
              <button
                type="submit"
                disabled={isSendingLink || !magicEmail.trim()}
                className="w-full bg-primary-500 hover:bg-primary-600 disabled:opacity-50 disabled:cursor-not-allowed text-white font-medium py-2.5 px-4 rounded-lg transition-colors"
              >
                {isSendingLink ? "Sending..." : "Send login link"}
              </button>
            </form>
          )}

          <div className="relative my-5">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-border"></div>
            </div>
            <div className="relative flex justify-center text-xs">
              <span className="bg-surface px-2 text-muted">or</span>
            </div>
          </div>

          <form onSubmit={handlePublicLogin} className="space-y-3">
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Enter access code"
              className="w-full bg-surface-elevated border border-border rounded-lg px-4 py-2.5 text-foreground placeholder:text-muted focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            />
            <button
              type="submit"
              disabled={isLoading || !password.trim()}
              className="w-full bg-surface-elevated hover:bg-surface border border-border disabled:opacity-50 disabled:cursor-not-allowed text-foreground font-medium py-2.5 px-4 rounded-lg transition-colors"
            >
              {isLoading ? "Verifying..." : "Access as Guest"}
            </button>
          </form>

          <p className="mt-5 text-xs text-muted-foreground text-center leading-relaxed">
            @story.inc accounts are automatically approved.
            <br />
            Others require an invitation or access code.
          </p>
        </div>
      </div>
    </div>
  );
}

function GoogleIcon() {
  return (
    <svg className="w-5 h-5" viewBox="0 0 24 24">
      <path
        fill="#4285F4"
        d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
      />
      <path
        fill="#34A853"
        d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
      />
      <path
        fill="#FBBC05"
        d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
      />
      <path
        fill="#EA4335"
        d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
      />
    </svg>
  );
}
